import React, { useCallback } from 'react';
import { useHistory } from 'react-router';
import styled from 'styled-components';
import { MainArea, TopArea, Page, Spinner, Error } from '@salecycle/ui-component-library';
import { Section } from 'components/experimental/Section/Section';
import { ActiveTab } from '../../../context/OnsiteCampaignSectionState';
import { CampaignHeaderBar } from './CampaignHeaderBar';
import { CampaignTabs } from './CampaignTabs';
import { useTranslation } from 'react-i18next';
import { CampaignFooterArea } from './CampaignFooterArea';
import { useOnsiteCampaignUrlParams } from 'sections/onsite-campaigns/hooks/useOnsiteCampaignUrlParams';
import { useConfirmUnsavedChangesModal } from '../tabs/CampaignWorkflowTab/contexts';
import { useHasUnsavedChanges } from '../tabs/CampaignWorkflowTab/state/CampaignSettings/selectors/useHasUnsavedChanges';

const SpinnerWrapper = styled.div`
  height: 100%;
  width: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
`;

const CampaignBody = styled.div`
  flex-grow: 1;
`;

interface Props {
  activeTab: ActiveTab;
  isLoading?: boolean;
  isError?: boolean;
  children: React.ReactNode;
}

export function CampaignMainArea(props: Props) {
  const { t } = useTranslation('campaigns');
  const history = useHistory();
  const { siteId } = useOnsiteCampaignUrlParams();
  const { openConfirmUnsavedChangesModal, setNextAction } = useConfirmUnsavedChangesModal();
  const hasUnsavedChanges = useHasUnsavedChanges();

  const onExitButtonClicked = useCallback(() => {
    const backToCampaignList = () => history.push(`/sites/${siteId}/campaigns/onsite`);
    if (hasUnsavedChanges) {
      setNextAction(backToCampaignList);
      openConfirmUnsavedChangesModal();
    } else {
      backToCampaignList();
    }
  }, [hasUnsavedChanges, history, openConfirmUnsavedChangesModal, setNextAction, siteId]);

  return (
    <MainArea>
      <TopArea>
        <CampaignHeaderBar isError={props.isError} onExitButtonClicked={onExitButtonClicked} />
      </TopArea>

      <Page padding={0} style={{ display: 'flex', flexDirection: 'column' }}>
        {props.isLoading && !props.isError && (
          <SpinnerWrapper>
            <Spinner />
          </SpinnerWrapper>
        )}
        {!props.isLoading && props.isError && <Error heading={t('onsite.errorLoadingCampaigns')} />}
        {!props.isLoading && !props.isError && (
          <>
            <Section alignSelf="center">
              <CampaignTabs siteId={siteId} activeTab={props.activeTab} />
            </Section>
            <CampaignBody>{props.children}</CampaignBody>
            <CampaignFooterArea />
          </>
        )}
      </Page>
    </MainArea>
  );
}
